<template>
  <div
    class="project-story ai-story project-case-study"
    :style="{ '--case-chrome': `${chromeHeight}px` }"
  >
    <div class="case-hero">
      <ProjectPageHeader project-id="searchstory" context="Personal project">
        <h1>AI × Self</h1>
        <p class="project-subtitle">
          An AI-driven personal archive
        </p>
      </ProjectPageHeader>
      <ProjectShowcase
        contextual
        :show-controls="false"
        project-id="searchstory"
        scene="Exploring the archive"
        id="archive-overview-illustration"
        class="story-product-art"
      />
    </div>
    <div class="case-opening">
      <p class="lede">
        Visitors can ask AI a question about my work, ideas or experiences.
        AI connects material from this site into a <AiViewTerm />, an illustrated
        article that answers their question and includes the original work.
        Each <AiViewTerm /> also becomes an exhibition visitors can walk through,
        and the <ArchiveTerm kind="gallery" /> brings shared exhibitions together.
      </p>
    </div>

    <ProjectChapterNav
      :start-number="0"
      ref="chapterNav"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />

    <article class="project-narrative case-narrative">
      <section id="case-overview" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>00</span> The project</span>
          <h2 id="one-archive-three-ways-to-explore" data-source-anchor="one-archive-three-ways-to-explore">An archive open to different perspectives</h2>
        </header>
        <div class="case-copy case-overview-intro" data-case-reveal>
          <p>
            A personal site usually reflects how its author has organised
            their life. Work goes under projects, an experience under a date,
            a thought into a piece of writing. A visitor’s question can cross
            those boundaries and connect things I recorded years apart and
            for different reasons.
          </p>
          <p>
            AI can read across these records and develop connections around
            someone’s question. Those readings can also change how I understand
            my own experiences.
          </p>
          <p>
            I built <ArchiveTerm kind="search" /> to find original passages and
            <AiAskTerm /> to answer questions with sources. <AiViewTerm /> connects
            my work, ideas and experiences into a longer narrative, presented as
            an article or an exhibition. Visitors can save their <AiViewTerm plural />
            and share them in the <ArchiveTerm kind="gallery" />.
          </p>
        </div>
        <ArchiveInteractionDemo id="case-experience" class="case-interaction-overview" />
        <div class="impl-details case-implementation case-system-details" data-case-reveal>
          <h3 id="system-architecture" class="implementation-heading" data-source-anchor="system-architecture">
          <button type="button" class="impl-details-toggle"
            :aria-expanded="architectureDetailsOpen.toString()" aria-controls="system-details-panel"
            @click="architectureDetailsOpen = !architectureDetailsOpen">
            <span>System architecture</span><span class="impl-details-caret" aria-hidden="true"></span>
          </button>
          </h3>
          <div id="system-details-panel" class="impl-details-panel"
            :class="{ 'is-open': architectureDetailsOpen }" :inert="!architectureDetailsOpen" :aria-hidden="!architectureDetailsOpen">
            <div class="impl-details-content">
                <div class="case-pair case-architecture-pair">
                  <div class="case-copy">
                    <p>At build time, the site prepares passages, embeddings and a component catalogue. <ArchiveTerm kind="search" /> runs in the browser. Cloudflare Workers supply context to Gemini for <AiAskTerm /> answers and <AiViewTerm /> compositions. MiniLM creates the search embeddings locally through Transformers.js, and Fish Audio provides narration in my AI voice. The text API makes the public material available to visitors’ own agents.</p>
                    <p>A shared JSON schema connects each <AiViewTerm /> to its Vue article and Three.js exhibition. Cloudflare D1 stores saved readings and source snapshots, while R2 holds their media. The <ArchiveTerm kind="gallery" /> brings the public readings together.</p>
                  </div>
                  <div class="case-architecture-figure">
                    <ProjectArchitecture project-id="searchstory" inline contextual :show-controls="false" />
                  </div>
                </div>
            </div>
          </div>
        </div>
      </section>
      <section
        id="case-search"
        class="case-section chapter-search"
        data-case-chapter
      >
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span> <ArchiveTerm kind="search" /></span>
          <h2>Find a passage in its original context</h2>
        </header>
        <div class="case-pair case-search-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              As the site grew, I added <ArchiveTerm kind="search" /> so visitors could
              quickly find relevant material across its pages. Each result shows
              a matching passage and opens its place on the original page.
            </p>
            <p>
              <ArchiveTerm kind="search" /> combines lexical and semantic retrieval.
              Word matching finds names and technical terms, while embeddings
              find passages with related meaning. A query about translating
              business requirements into software can therefore surface my work
              at HSBC without naming the company.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content case-search-demos">
              <SearchPalette embedded fixed-mode="search" initial-query="explore" />
            </div>
          </div>
        </div>
        <div class="impl-details case-implementation" data-case-reveal>
          <button
            type="button"
            class="impl-details-toggle"
            :aria-expanded="searchDetailsOpen.toString()"
            aria-controls="search-details-panel"
            @click="searchDetailsOpen = !searchDetailsOpen"
          >
            <span>How <ArchiveTerm kind="search" /> finds related material</span>
            <span class="impl-details-caret" aria-hidden="true"></span>
          </button>
          <div id="search-details-panel" class="impl-details-panel"
            :class="{ 'is-open': searchDetailsOpen }" :inert="!searchDetailsOpen" :aria-hidden="!searchDetailsOpen">
            <div class="impl-details-content">
              <div class="implementation-intro">
                  <p>At build time, all-MiniLM-L6-v2 encodes each passage as a 384-dimensional embedding. I quantise these vectors to 8-bit values to keep the download compact. Transformers.js runs the same model in the browser to embed a query, then cosine similarity ranks the passages by meaning.</p>
                  <p>Fuse.js supplies the word-match ranking. Weighted reciprocal-rank fusion combines it with the semantic ranking, giving word matches twice the weight. Using rank positions lets the two methods contribute despite their different scoring scales.</p>
              </div>
              <div id="retrieval-before-generation" class="interactive-surface retrieval-surface" aria-label="Compare word and meaning matches">
                <RetrievalLab v-if="searchDetailsOpen" initial-query="explore" remember-comparison />
              </div>
              <div class="implementation-notes">
                  <p>Search excludes semantic matches below 0.24 cosine similarity. Adjust the threshold in the comparison to see which passages remain.</p>
              </div>
            </div>
          </div>
        </div>
        <div id="case-map" class="case-search-map">
          <header class="case-subsection-heading" data-case-reveal>
            <h3 id="seeing-the-archive-as-a-space" data-source-anchor="seeing-the-archive-as-a-space">The archive as a space</h3>
          </header>
          <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              The embedding map gives the archive a spatial form. Projects,
              research and personal writing appear together as a field of points.
              Their positions make patterns of similarity visible across material
              that lives on separate pages.
            </p>
            <p>
              UMAP, a dimensionality-reduction method, arranges these embeddings in two or
              three dimensions. Each dot represents a passage. Nearby dots tend to represent
              passages with similar meanings. A visitor can add a question to see where it
              falls within the material already here.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div
                id="shared-memory"
                class="interactive-surface embedding-surface"
              >
                <EmbeddingMap initial-view-mode="3d" two-finger-touch />
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>
      <section id="case-ask" class="case-section chapter-ask" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span> <AiAskTerm /></span>
          <h2 id="grounded-ask">
            <AiAskTerm /> brings related passages into one answer
          </h2>
        </header>
        <div class="case-pair case-ask-pair">
          <div class="case-copy" data-case-reveal>
            <p id="cite" data-source-anchor="cite">
              I built <AiAskTerm /> to let the site respond directly to visitors’
              questions. It uses retrieval-augmented generation (RAG), giving a
              language model relevant material from my work, ideas and experiences
              to draw on.
            </p>
            <p>
              The model returns a structured response, which the interface presents
              as an answer with a linked source list and follow-up questions.
              Visitors can also listen to the answer in an AI voice trained on
              recordings of my own voice.
            </p>
            <div class="impl-details case-implementation">
              <button type="button" class="impl-details-toggle"
                :aria-expanded="askDetailsOpen.toString()" aria-controls="ask-details-panel"
                @click="askDetailsOpen = !askDetailsOpen">
                <span>How <AiAskTerm /> uses the source material</span><span class="impl-details-caret" aria-hidden="true"></span>
              </button>
              <div id="ask-details-panel" class="impl-details-panel"
                :class="{ 'is-open': askDetailsOpen }" :inert="!askDetailsOpen" :aria-hidden="!askDetailsOpen">
                <div class="impl-details-content">
                  <div class="implementation-copy">
                    <p id="set-the-scope" data-source-anchor="set-the-scope">
                      <!-- Preserve destinations cited before the numbered strip was removed. -->
                      <span id="find" aria-hidden="true"></span><span id="write" aria-hidden="true"></span>
                      The browser assembles the public site passages, including
                      writing selected through retrieval, and sends them with the
                      question to a Cloudflare Worker. The Worker supplies this
                      context to Gemini. Numbered passages let the model cite its sources.
                    </p>
                    <p>
                      The answer carries those citation numbers, while separate
                      response sections contain follow-up questions, navigation
                      actions and a possible <AiViewTerm /> offer. The browser parses
                      these into interface elements and builds the source list,
                      grouping citations to the same location. Navigation actions
                      map to the site’s registered routes and controls.
                    </p>
                    <p>
                      I also embed the proposed follow-up questions and compare
                      them with the archive, using a 0.30 cosine-similarity threshold
                      to select suggestions supported by site material.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <SearchPalette
                embedded
                fit-content
                fixed-mode="ask"
                initial-query="How does Henry use AI in his personal website?"
                :initial-answer="savedAsk.answer"
                :initial-sources="savedAsk.sources"
                :initial-follow-ups="savedAsk.followUps"
              />
            </div>
          </div>
        </div>
        <div class="case-ask-examples" data-case-reveal>
          <header class="case-subsection-heading"><h3>How does <AiAskTerm /> handle different kinds of questions?</h3></header>
          <div class="case-copy"><p><AiAskTerm /> classifies questions to choose an appropriate response. For questions about using the site, it offers a link or action. For questions about my work and ideas, it explains the relevant material and can offer a <AiViewTerm /> for a fuller interpretation.</p></div>
          <AskQuestionExamples />
        </div>
      </section>
      <section
        id="case-view"
        class="case-section chapter-view"
        data-case-chapter
      >
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span> <AiViewTerm /></span>
          <h2 id="composed-views">
            <AiViewTerm /> reorganises the site around a question
          </h2>
        </header>
        <div class="case-pair case-art-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A <AiViewTerm /> brings together my work, ideas and experiences around
              a visitor’s question. AI connects the selected material into a
              narrative and places the original works within it for visitors to explore.
            </p>
            <p>
              Different questions bring out different relationships in the same
              archive. A reading about learning might connect a research study
              with an education project, revealing something neither piece
              explains on its own.
            </p>
            <ProjectExperienceAction class="case-compose-entry" data-press-intent="custom" aria-haspopup="dialog" @click="openViewPalette">Compose a <AiViewTerm /></ProjectExperienceAction>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <ProjectShowcase
                contextual
                :show-controls="false"
                project-id="searchstory"
                scene="A composed View"
              id="archive-view-illustration"
                class="story-product-art"
              />
            </div>
          </div>
        </div>
        <header class="case-subsection-heading case-view-subsection" data-case-reveal>
          <h3>How does AI build an interpretation around a question?</h3>
        </header>
        <div class="case-pair case-technical-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              I registered written passages, image groups and working demos as
              reusable components. The model selects and places them within the
              narrative, explaining how each supports its interpretation. Visitors
              can read, examine and interact with the original work as they follow
              the argument.
            </p>
            <p>
              The model can use ideas beyond the archive to interpret my material.
              For example, it can read my travel writing through a theory of identity
              and explain what that reveals about my sense of home.
            </p>
            <p>
              An image-generation model creates an illustration that captures
              the <AiViewTerm />’s central idea.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <EmbeddedSharedView
                share-id="PNZPdB__yBeyYjkF"
                view-label="View"
              />
            </div>
          </div>
        </div>
        <header class="case-subsection-heading case-view-subsection" data-case-reveal>
          <h3>A reading to save and share</h3>
        </header>
        <div class="case-pair case-technical-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A saved <AiViewTerm /> brings together its question, narrative,
              sources, illustrations and audio. Its permanent link lets the
              creator return to that version or share it with someone else.
            </p>
            <p>
              Creators can also publish a <AiViewTerm /> in the shared collection.
              Other visitors can explore these readings and see the archive
              through someone else’s perspective.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content"><SharedViewPreviews /></div>
          </div>
        </div>
      </section>
      <section id="case-gallery" class="case-section case-gallery-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>04</span> <ArchiveTerm kind="gallery" /></span>
          <h2>Every <AiViewTerm /> becomes an exhibition</h2>
        </header>

        <div class="case-gallery-comparison" data-case-reveal>
          <span id="article-and-exhibition-keep-the-same-place" data-source-anchor="article-and-exhibition-keep-the-same-place"></span>
          <ViewPresentationJourneyDemo>
            <template #lead>
              <div class="case-copy case-exhibition-intro">
              <p>
                I turned each <AiViewTerm /> into an exhibition so visitors could
                explore its perspective by moving among the original works.
                The narrative guides the visit, and visitors can pause to explore
                a photograph, passage or project and make connections of their own.
              </p>
              <p>
                Each section becomes a room, with photographs on walls, writing
                on reading stands and places on a globe. The layout responds
                to the number and type of works in each section, connecting the
                rooms in reading order. Each interpretation has its own route
                through the archive.
              </p>
              <ProjectExperienceAction class="case-exhibition-entry" to="/view/PNZPdB__yBeyYjkF?presentation=museum&temporaryPresentation=1" target="_blank" rel="noopener">Visit an exhibition</ProjectExperienceAction>
              </div>
              <ExhibitionTicket id="sample-exhibition-ticket" :view="ticketSample" sample />
            </template>
          </ViewPresentationJourneyDemo>
        </div>

        <div id="the-exhibition-is-designed-as-one-experience" data-source-anchor="the-exhibition-is-designed-as-one-experience" class="case-exhibition-design" data-case-reveal>
          <GalleryExperienceStudy :groups="['exhibition', 'atmosphere']" />
        </div>

        <header class="case-subsection-heading case-gallery-collection-heading" data-case-reveal>
          <h3 class="case-gallery-identity"><GalleryMark />The View Gallery</h3>
        </header>
        <GalleryDesignStudy id="gallery-design-study" data-case-reveal />
        <section id="gallery-video" data-source-anchor="gallery-video" class="case-gallery-video" aria-labelledby="case-gallery-video-title" data-case-reveal>
          <header class="case-subsection-heading">
            <h3 id="case-gallery-video-title">A video introduction</h3>
          </header>
          <GalleryFilm />
        </section>
      </section>
      <section id="case-architecture" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>05</span> Access</span>
          <h2>Access for visitors and agents</h2>
        </header>
        <div class="case-pair case-access-pair">
          <div class="case-copy" data-case-reveal>
            <h3 id="available-across-the-site" data-source-anchor="available-across-the-site">On desktop and phone</h3>
            <p>Visitors can open <ArchiveTerm kind="search" />, <AiAskTerm />, <AiViewTerm /> and the embedding map from the header on any page. Home also has a question field, and wide screens add a search control beside the page.</p>
            <p>The <ArchiveTerm kind="gallery" /> and <ArchiveTerm kind="exhibition" plural /> use different camera views, reading panels and controls on desktop, portrait phones and landscape phones. The demonstrations below show these layouts alongside the ways visitors can open the site’s tools.</p>
          </div>
        </div>
        <div data-case-reveal><SiteAccessStudy /></div>
        <div class="case-pair case-agent-access" data-case-reveal>
          <div class="case-copy case-agent-intro">
            <h3>Exploring with your own agent</h3>
            <p id="bring-your-own-agent" data-source-anchor="bring-your-own-agent">Visitors can give their agent henryw.me to explore my work, ideas and experiences. A reading guide and public text API provide the archive and links to the original pages, so the agent can read, connect and cite the material in its own response.</p>
          </div>
          <AgentConversationDemo />
        </div>
        <div class="impl-details case-implementation" data-case-reveal>
          <button type="button" class="impl-details-toggle"
            :aria-expanded="agentDetailsOpen.toString()" aria-controls="agent-details-panel"
            @click="agentDetailsOpen = !agentDetailsOpen">
            <span>How agents read the archive</span><span class="impl-details-caret" aria-hidden="true"></span>
          </button>
          <div id="agent-details-panel" class="impl-details-panel"
            :class="{ 'is-open': agentDetailsOpen }" :inert="!agentDetailsOpen" :aria-hidden="!agentDetailsOpen">
            <div class="impl-details-content">
              <div class="implementation-intro">
                <p>I export the site’s public writing as complete text and JSON records at build time. An agent can read the archive through ordinary HTTP GET requests, then use its own model to interpret the material. Reading whole pieces gives it the context to make connections across projects, research and personal writing.</p>
                <p>The static downloads and text API use the same source records. Each record carries the full text, a stable ID and a link to its original place on the site. A content revision identifies the version the agent is reading.</p>
              </div>
              <div class="agent-reading-flow" aria-label="How an agent discovers, reads and cites the archive">
                <div class="agent-reading-step">
                  <div class="agent-reading-visual agent-discovery" aria-hidden="true">
                    <span class="agent-site">henryw.me</span>
                    <span class="agent-file"><svg viewBox="0 0 20 24"><path d="M3 1h9l5 5v17H3zM12 1v6h5M6 12h8M6 16h6" /></svg>Reading guide</span>
                  </div>
                  <h4>Discover the interface</h4>
                  <p>The page’s HTML links and <code>llms.txt</code> point to a reading guide and an OpenAPI definition describing the available requests.</p>
                </div>
                <div class="agent-reading-step">
                  <div class="agent-reading-visual agent-texts" aria-hidden="true">
                    <span>Research</span><span>Projects</span><span>Fragments</span>
                  </div>
                  <h4>Read the material</h4>
                  <p>The agent can download the whole archive or request complete sections and individual sources through the API, according to its task and context budget.</p>
                </div>
                <div class="agent-reading-step">
                  <div class="agent-reading-visual agent-citations" aria-hidden="true">
                    <span class="agent-answer-lines"><i></i><i></i><i></i></span>
                    <span class="agent-source-links"><span>Research</span><span>Original writing</span></span>
                  </div>
                  <h4>Connect and cite</h4>
                  <p>The agent develops its answer across the material. Original page links let the reader follow a citation to the passage, image or interactive work it discusses.</p>
                </div>
              </div>
              <AgentDocuments />
            </div>
          </div>
        </div>
      </section>
      <section id="case-over-time" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>06</span> Afterword</span>
          <h2 id="what-the-case-gives-back" data-source-anchor="what-the-case-gives-back">What can I learn from a website about myself?</h2>
        </header>
        <div class="case-copy case-over-time-copy" data-case-reveal>
          <p>
            Some things in my own life are easier to see through someone else’s
            questions. The experiences are familiar to me, yet the relationships
            a visitor asks about may be ones I have never considered. AI can
            develop those relationships into a reading I can follow, giving me
            a chance to reconsider what I already know about myself.
          </p>
          <p>
            My own perspective also changes with time. A passage I wrote years
            ago can mean something different after a later experience. Revisiting
            that passage helps me consider what has changed in my thinking.
            I become a reader of my own archive and continue to learn about
            the life I have recorded.
          </p>
          <p>
            Saved <AiViewTerm plural /> preserve the questions people asked and
            the readings they prompted. Over time, these become part of the
            archive themselves. In a few years, I will be able to revisit how
            these experiences were interpreted at this point in my life.
          </p>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
import AgentDocuments from "./ui/AgentDocuments.vue";
import ProjectShowcase from "./ui/ProjectShowcase.vue";
import ArchiveInteractionDemo from "./ui/ArchiveInteractionDemo.vue";
import AskQuestionExamples from "./ui/AskQuestionExamples.vue";
import AgentConversationDemo from "./ui/AgentConversationDemo.vue";
import ProjectArchitecture from "./ui/ProjectArchitecture.vue";
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import "../assets/css/resource-action.css";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";
import EmbeddingMap from "./EmbeddingMap.vue";
import RetrievalLab from "./view/RetrievalLab.vue";
import SharedViewPreviews from "./view/SharedViewPreviews.vue";
import EmbeddedSharedView from "./view/EmbeddedSharedView.vue";
import SearchPalette from "./SearchPalette.vue";
import AiAskTerm from "./ui/AiAskTerm.vue";
import AiViewTerm from "./ui/AiViewTerm.vue";
import ArchiveTerm from "./ui/ArchiveTerm.vue";
import ExhibitionTicket from "./view/ExhibitionTicket.vue";
import GalleryMark from "./view/GalleryMark.vue";
import ticketSample from "../data/projectTicketSample.json";
import ViewPresentationJourneyDemo from "./ui/ViewPresentationJourneyDemo.vue";
import SiteAccessStudy from './ui/SiteAccessStudy.vue';
import GalleryExperienceStudy from "./ui/GalleryExperienceStudy.vue";
import GalleryDesignStudy from "./ui/GalleryDesignStudy.vue";
import GalleryFilm from "./ui/GalleryFilm.vue";
import ProjectExperienceAction from "./ui/ProjectExperienceAction.vue";

// Keep the expanded page geometry when a reader visits a source and returns.
const implementationState = {
  searchDetailsOpen: false,
  askDetailsOpen: false,
  agentDetailsOpen: false,
  architectureDetailsOpen: false,
};

export default {
  name: "SearchStoryPage",
  components: {
    AgentDocuments,
    ArchiveInteractionDemo,
    AgentConversationDemo, AskQuestionExamples,
    ProjectChapterNav,
    ProjectShowcase,
    ProjectArchitecture,
    ProjectPageHeader,
    AiAskTerm,
    AiViewTerm,
    ArchiveTerm,
    EmbeddingMap,
    RetrievalLab,
    SharedViewPreviews,
    EmbeddedSharedView,
    SearchPalette,
    ViewPresentationJourneyDemo, ExhibitionTicket, GalleryMark,
    GalleryExperienceStudy, GalleryDesignStudy, GalleryFilm,
    SiteAccessStudy,
    ProjectExperienceAction,
  },

  mixins: [projectCaseStudyMixin],
  data() {
    return {
      ticketSample,
      ...implementationState,
      chapters: [
        { id: "case-overview", label: "Overview", tone: "overview" },
        { id: "case-search", label: "Search", tone: "search" },
        { id: "case-ask", label: "Ask", tone: "ask" },
        { id: "case-view", label: "View", tone: "view" },
        { id: "case-gallery", label: "Gallery", tone: "view" },
        { id: "case-architecture", label: "Access", tone: "overview" },
        { id: "case-over-time", label: "Afterword", tone: "overview" },
      ],
      savedAsk: {
        answer: `Henry uses AI in his personal website to explore how AI can reorganize the underlying material around what a visitor wants to understand, and to help him understand and record himself from different angles [1]. The site's AI interface allows visitors to explore its content through search, answers with citations, and "Views" that compose new pages around specific questions or themes [2][3]. This system uses retrieval-augmented generation (RAG) to find relevant passages and generate answers, and it can also suggest follow-up questions [4].`,
        sources: [
          { page: "About", title: "About Me", route: "/about", anchor: "" },
          {
            page: "AI × Self",
            title: "AI × Self",
            route: "/aipersonalarchive",
            anchor: "",
          },
          {
            page: "AI × Self",
            title: "Ask brings related passages into one answer",
            route: "/aipersonalarchive",
            anchor: "ask-brings-related-passages-into-one-answer",
          },
          {
            page: "AI × Self",
            title: "Cite",
            route: "/aipersonalarchive",
            anchor: "cite",
          },
        ],
        followUps: [
          "How does the search feature work on Henry's site?",
          'What are "Views" on Henry\'s website?',
          "How does Henry use AI in his other projects?",
        ],
      },
    };
  },
  beforeUnmount() {
    for (const key of Object.keys(implementationState)) implementationState[key] = this[key];
  },
  methods: {
    openViewPalette() {
      window.dispatchEvent(new CustomEvent("open-site-view"));
    },
  },
};
</script>

<style scoped>
.case-overview-intro { max-width:50rem; }
@media(max-width:760px){.case-gallery-demo-pair{grid-template-columns:1fr!important}}
.case-ask-examples{margin-top:clamp(40px,5vw,72px);}
.case-agent-access { align-items: start; }
.case-agent-intro { min-width: 0; }
.case-agent-intro h3 { margin-top: 0; }
.case-architecture-pair { align-items: start; }
.case-agent-access { margin-top: clamp(3rem, 6vw, 5rem); }
.case-gallery-identity{display:flex;align-items:center;gap:.8rem;}
.case-gallery-identity .gallery-mark{width:2.1rem;height:2.1rem;flex:none;}
.case-exhibition-intro p + p{margin-top:1.1rem;}
.case-compose-entry,.case-exhibition-entry{margin-top:.8rem;}
.case-gallery-comparison :deep(.view-journey-lead){align-items:center;}
.case-gallery-collection-heading { margin-top: clamp(4rem, 8vw, 7rem); }
.case-gallery-video { margin-top: clamp(4rem, 8vw, 7rem); }
.case-gallery-collection-heading h3 { max-width:none;font-size:clamp(1.8rem,3vw,2.5rem); }
.case-architecture-pair h3 { margin-top: 0; }
.case-architecture-figure { width: 100%; max-width: 34rem; justify-self: center; }
.case-architecture-figure :deep(.project-architecture.is-inline) { margin: 0; }
.case-architecture-figure :deep(.architecture-inline) { width: 100%; }

.case-view-subsection { margin-top: clamp(2.5rem, 5vw, 4rem); }
.chapter-view .case-view-subsection + .case-technical-pair { margin-top: 0; }
.case-technical-pair { margin-top: clamp(1.8rem, 3vw, 2.6rem); }
.chapter-search .case-technical-pair { margin-top: 1.3rem; }
.case-search-map { margin-top: clamp(2.25rem, 3.8vw, 3.25rem); }
.case-ask-pair > .case-stage,
.case-ask-pair .case-stage-content { display: grid; }

.case-gallery-section { --gallery-rule: color-mix(in srgb, var(--site-text-primary) 16%, transparent); }
.case-gallery-demo-pair { align-items: start; grid-template-columns: minmax(0,.85fr) minmax(0,1.15fr); }
.case-gallery-live-stage { min-width: 0; }
.case-gallery-live-stage :deep(.view-gallery-live-stage) { width: 100%; }
.case-gallery-plan { max-width: 62rem; margin: clamp(2rem,4vw,3.5rem) auto 0; }
.case-gallery-comparison,
.case-gallery-immersion { margin-top: clamp(2.25rem, 3.8vw, 3.25rem); }
.case-subsection-heading { display: block; margin-bottom: clamp(1.6rem, 3vw, 2.6rem); }
.case-subsection-heading > span { color: var(--site-text-tertiary); font: 500 .68rem/1.4 "Quattrocento Sans", sans-serif; letter-spacing: .09em; text-transform: uppercase; }
.case-subsection-heading h3 { max-width: 34rem; margin: 0; font: 400 clamp(1.28rem, 1.75vw, 1.55rem)/1.25 "PT Serif", Georgia, serif; letter-spacing: -.018em; }
.case-gallery-immersion-copy { display: block; }
.case-gallery-immersion-copy .case-subsection-heading { display: block; margin: 0 0 20px; }
.case-gallery-immersion-copy .case-subsection-heading h3 { margin-top: 0; }
.case-gallery-immersion-copy .case-copy { max-width: 52rem; }
@media (max-width: 1100px) {
  .case-gallery-immersion-copy { gap: 2rem; }
}
@media (max-width: 900px) {
  .case-gallery-immersion-copy { grid-template-columns: 1fr; gap: 1rem; max-width: 43rem; margin-inline: auto; }
}
@media (max-width: 760px) {
  .case-subsection-heading,
  .case-gallery-immersion-copy { grid-template-columns: 1fr; gap: 1rem; }
  .case-gallery-demo-pair { align-items: start; }
  .case-gallery-comparison,
  .case-gallery-immersion { margin-top: 2.5rem; }
}
.case-access-pair{display:block;}
.case-access-pair .case-copy{max-width:52rem;}
.case-exhibition-intro{font-family:'PT Serif',Georgia,serif;}
.case-implementation { margin-top: 2rem; }
.case-implementation .impl-details-toggle { justify-content: flex-start; width: fit-content; max-width: 100%; gap: 1rem; }
.case-implementation .impl-details-caret { flex: none; }
.case-system-details { margin-top: 2.5rem; }
.implementation-heading { margin: 0; font: inherit; }
.case-system-details .case-architecture-pair { margin-top: 1rem; }
.implementation-intro { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 2.5rem; margin: .5rem 0 1.2rem; }
.implementation-copy { max-width: 50rem; }
.implementation-notes { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 2rem; margin-top: 1.4rem; }
.implementation-intro p, .implementation-notes p { margin: .5rem 0; }
.agent-reading-flow { --agent-paper: #f8f7f5; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 2rem; padding: 1.4rem 1.6rem 1.6rem; background: color-mix(in srgb, var(--case-ink) 4%, transparent); }
.agent-reading-step { min-width: 0; display: flex; flex-direction: column; }
.agent-reading-step h4 { margin: 1rem 0 0; color: var(--case-ink); font: 400 1.1rem/1.4 'PT Serif', Georgia, serif; }
.agent-reading-step p { margin: .6rem 0 0; font-size: .9rem; line-height: 1.65; }
.agent-reading-step code { font-size: .84em; overflow-wrap: anywhere; }
.agent-reading-visual { height: 6.5rem; display: flex; justify-content: center; align-items: center; color: var(--case-muted); font: 13px/1.4 'Quattrocento Sans', sans-serif; }
.agent-discovery { flex-direction: column; }
.agent-site { padding: 5px 14px; border: 1px solid var(--case-rule); border-radius: 5px; color: var(--case-ink); }
.agent-file { display: flex; align-items: center; gap: 8px; position: relative; margin-top: 23px; }
.agent-file::before { content: ''; position: absolute; width: 1px; height: 16px; left: 50%; top: -21px; background: var(--case-rule); }
.agent-file svg { width: 16px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1; }
.agent-texts { gap: 0; }
.agent-texts > span { display: flex; align-items: flex-end; width: 5rem; height: 4.5rem; padding: 10px 7px; border: 1px solid var(--case-rule); background: var(--agent-paper); font-size: 11px; }
.agent-texts > span + span { margin-left: -1.2rem; transform: translateY(7px); }
.agent-texts > span:last-child { transform: translateY(14px); }
.agent-citations { flex-direction: column; align-items: stretch; padding: 0 14px; }
.agent-answer-lines { display: grid; gap: 8px; }
.agent-answer-lines i { height: 2px; background: var(--case-rule); }
.agent-answer-lines i:nth-child(2) { width: 87%; }
.agent-answer-lines i:nth-child(3) { width: 68%; }
.agent-source-links { display: flex; gap: 12px; margin-top: 17px; color: var(--site-accent); font-size: 11px; }
.agent-source-links > span { text-decoration: underline; text-underline-offset: 3px; }
@media (max-width: 760px) {
  .implementation-intro, .implementation-notes { grid-template-columns: 1fr; gap: .3rem; }
  .agent-reading-flow { grid-template-columns: 1fr; gap: 2rem; padding: 1.2rem; }
  .agent-reading-visual { height: 5.5rem; max-width: 17rem; width: 100%; align-self: center; }
}
body.dark-mode .agent-reading-flow { --agent-paper: #272727; }
</style>
